import { useForm, FormProvider } from "react-hook-form";
import { Clock, X } from "lucide-react";
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import { addMinutes, format } from "date-fns";
import { TimelineActivityType } from "../../../types/enums/TimelineActivityType";
import { EntityStatus } from "@repo/types/enums";
import type { ExecutionResponse } from "@repo/types/responses";
import type AddUpdateTimelineActivityProps from "../../../types/ui/AddEditTimelineActivityProps";
import type { AddUpdateTimelineActivityViewModel } from "../../../types/viewModels/timelineActivity/AddUpdateTimelineActivityViewModel";
import { useDialog } from "../../../hooks/contexts/useDialog";
import { useAddUpdateTimelineActivity } from "../../../hooks/queries/useTimelineActivity";
import TextEditor from "../../ui/textEditor/TextEditor";
import Button from "../../ui/button/Button";
import { DateWithTimeRangePicker } from "../../form/input/DateWithTimeRangePicker";

export default function AddUpdateTimelineActivityContainer({
  ticket,
  activity,
  activityType,
  onClose,
}: AddUpdateTimelineActivityProps) {
  const dialog = useDialog();
  const { mutateAsync: addUpdateTimelineActivity } =
    useAddUpdateTimelineActivity();

  const handleAddUpdateActivity = async (
    model: AddUpdateTimelineActivityViewModel,
  ): Promise<ExecutionResponse> => {
    const res = await addUpdateTimelineActivity(model);
    if (res) {
      dialog.show({
        title: "Activity Status",
        message: res.Message,
        type: res.Result ? "success" : "error",
        confirmText: "Ok",
      });
      onClose();
    }

    return res;
  };

  const dateNow = new Date();

  const defaultValues: AddUpdateTimelineActivityViewModel = {
    Id: activity ? activity.Id : null,
    ActivityTypeKeyValuePairId: activity
      ? activity.ActivityTypeKeyValuePairId
      : (activityType?.Id ?? 0),
    Content: activity ? activity.Content : "",
    TicketId: activity ? activity.TicketGuid : ticket.TicketGuid,
    Status: activity ? activity.Status : EntityStatus.Active,
    MentionUserIds: activity ? activity.MentionUserIds : [],
  };

  if (activityType?.enumValue !== TimelineActivityType.Note) {
    defaultValues.ScheduledDate = activity?.ScheduledDate
      ? activity.ScheduledDate
      : format(dateNow, "yyyy-MM-dd");
    defaultValues.ScheduledStartTime = activity?.ScheduledStartTime
      ? activity.ScheduledStartTime
      : format(dateNow, "HH:mm");
    defaultValues.ScheduledEndTime = activity?.ScheduledEndTime
      ? activity.ScheduledEndTime
      : format(addMinutes(dateNow, 30), "HH:mm");
  }

  const methods = useForm<AddUpdateTimelineActivityViewModel>({
    defaultValues: defaultValues,
  });

  return (
    <Dialog open={true} onClose={onClose}>
      <div
        className={
          "fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3"
        }
      >
        <DialogBackdrop className="fixed inset-0 bg-black/30" />

        {/* Floating Panel */}
        <DialogPanel className="fixed inset-0 flex items-end justify-end p-6 right-7 bottom-3">
          <div className="w-[600px] bg-white rounded-t shadow-xl border border-gray-200 overflow-hidden">
            <DialogTitle className="flex w-full items-center text-base p-0.5 pl-2 bg-gray-50 text-gray-600 truncate">
              {activityType && (
                <div className="basis-[96%]">{activityType.Value}</div>
              )}
              <div className="basis-[4%] ml-2">
                <Button
                  variant="ghost"
                  onClick={onClose}
                  className="hover:text-gray-700 cursor-pointer hover:bg-gray-200 rounded ml-auto"
                >
                  <X size={16} />
                </Button>
              </div>
            </DialogTitle>

            <FormProvider {...methods}>
              <TextEditor
                mentionsEnabled={true}
                templatesEnabled={true}
                attachmentsEnabled={
                  activityType?.enumValue !== TimelineActivityType.Note
                }
                submitButtonText={activity ? "Save" : "Add"}
                onSubmit={methods.handleSubmit(handleAddUpdateActivity)}
              >
                {activityType?.enumValue !== TimelineActivityType.Note && (
                  <div className="inline-flex items-center p-1 pl-2 m-2 cursor-pointer text-base hover:bg-gray-200 rounded">
                    <Clock size={14} className="mr-2 text-gray-600" />
                    <DateWithTimeRangePicker />
                  </div>
                )}
              </TextEditor>
            </FormProvider>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
